/**
 * Fxxl Print
 *
 * Filename: fxxl_print.ts
 *
 * @category  Fahrradxxl
 * @author    Janik Kemnade <j.kemnade@fahrrad-xxl.de>
 * @copyright 2026 Fahrrad-xxl.de
 */



/* 📜 INFORMATION
 * --------------------------------------------------------------------------------------------------------------------
 * Usage:
 * - Compare: dev/customer_fahrradxxl/custom/module/public/fxxl3/assets/js/module/compare.js:504 ( setupComparePrintLink )
 *
 */






/* 📦️ IMPORTS
 * ----------------------------------------------------------------------------------------------------------------- */
import FxxlCore from '@fxxl/core';
import FxxlError from '@fxxl/error';
import { FxxlDeviceBrowser } from '@fxxl/fxxl_device';






/* 📑 ENUMS & TYPES & CONSTANTS
 * ----------------------------------------------------------------------------------------------------------------- */
// Error Messages
enum ERROR_MESSAGE {
    SOMETHING_WENT_WRONG = 'Error: Fxxl Print: Something went wrong',
    ELEMENT_NOT_EXISTING = 'Error: Fxxl Print: Print Container does not exist',
    IN_PROCESS           = 'Error: Fxxl Print: Print in process',
}

// HTML Classes
enum HTMLCLASS {
    HIDE                          = 'fxxl-print-hidden',
    SHOW_BODY                     = 'fxxl-print-body',
    SHOW_ELEMENT                  = 'fxxl-print-element',
    SHOW_LOGO                     = 'fxxl-print-logo',
    SHOW_COPYRIGHT                = 'fxxl-print-copyright',
    CONTAINER_IMPRESSUM_COPYRIGHT = 'fxxl-impressums-and-copyright_copyright',
    ELEMENT_LOGO                  = 'fxxl-header__logo-link',
}


// Selectors
const SELECTOR = {
    ALL_BODY_ELEMENTS:              'body > *',
    ELEMENT_LOGO:                   `.${HTMLCLASS.ELEMENT_LOGO}`,
    CONTAINER_IMPRESSUM_COPYRIGHT:  `.${HTMLCLASS.CONTAINER_IMPRESSUM_COPYRIGHT}`,
};


// Time Values
const TIME_VALUE = {
    REVERT_DELAY: 600,
};







/* 💾 INTERFACES
 * ----------------------------------------------------------------------------------------------------------------- */
interface IFxxlPrintContent {
    mainContent: HTMLElement;
    logo?:       HTMLElement;
    copyright?:  HTMLElement;
}





/* ✒️ CLASS: FxxlPrint
 * ----------------------------------------------------------------------------------------------------------------- */
export default class FxxlPrint {

    /* 🏷️ VARS
     * ----------------------------------------------------------------------------------------- */
    private static readyForPrinting: boolean                        = true;
    private static isPrinting:       boolean                        = false;
    private static bodyElements:     NodeListOf<HTMLElement> | null = null;
    private static printFrame:       HTMLElement | null             = null;
    private static frameContent:     IFxxlPrintContent | null       = null;




    /* 🐙 STATIC MAIN: Print ( Wrapper: window.print )
     * --------------------------------------------------------------------------------------------
     * This is the main print method, preparing for the print operation and initiating window.print()
     *
     * @param printMainContent - The main content that needs to be printed
     * @returns void
     */
    public static async print(printMainContent: HTMLElement): Promise<void> {
        if (!printMainContent) {
            new FxxlError(ERROR_MESSAGE.ELEMENT_NOT_EXISTING);
            return;
        }
        if (FxxlPrint.isPrinting) {
            new FxxlError(ERROR_MESSAGE.IN_PROCESS);
            return;
        }

        // MARK: Print States
        FxxlPrint.readyForPrinting = true;
        FxxlPrint.isPrinting = true;

        // PREP: Print Document
        FxxlPrint.printPreTasks();
        FxxlPrint.printPrepareContent(printMainContent);
        FxxlPrint.printPrepareFrame();
        if (FxxlDeviceBrowser.isSafari()) {
            await FxxlPrint.printPreloadImages();
        }

        // EXEC: Print
        if (!FxxlPrint.readyForPrinting) {
            FxxlPrint.printCleanup();
            new FxxlError(ERROR_MESSAGE.SOMETHING_WENT_WRONG);
            return;
        }

        window.print();

        // REVERT & CLEANUP
        setTimeout(FxxlPrint.printCleanup, TIME_VALUE.REVERT_DELAY);
    }


    /**
     * Utility method to prepare the DOM state before initiating print
     *
     * @returns void
     */
    private static printPreTasks(): void {
        // SCROLL: To Top
        if (document.documentElement !== undefined) {
            document.documentElement.scrollTop = 0;
        } else {
            document.body.scrollTop = 0;
        }

        // GET: Body Elements
        FxxlPrint.bodyElements = document.querySelectorAll(SELECTOR.ALL_BODY_ELEMENTS);

        // SET: Body Var
        FxxlPrint.printFrame = document.body;
    }


    /**
     * Prepares the content for print: clone the main content, logo, and copyright
     *
     * @param printMainContent - The main content that needs to be printed
     * @returns Promise<void>
     */
    private static printPrepareContent(printMainContent: HTMLElement): void {
        const elementLogo = document.querySelector(SELECTOR.ELEMENT_LOGO);
        const elementCopyright = document.querySelector(SELECTOR.CONTAINER_IMPRESSUM_COPYRIGHT);

        // SET: Base
        FxxlPrint.frameContent = {
            mainContent: (printMainContent.cloneNode(true) as HTMLElement)
        };

        // ADD: Body -> Print Class
        FxxlPrint.frameContent.mainContent.classList.add(HTMLCLASS.SHOW_ELEMENT);

        // SET: LOGO
        if (elementLogo) {
            const logo: HTMLElement = (elementLogo?.cloneNode(true) as HTMLElement);
            logo.classList.add(HTMLCLASS.SHOW_LOGO);
            FxxlPrint.frameContent.logo = logo;
        }

        // SET: Copyright
        if (elementCopyright) {
            const copyright: HTMLElement = (elementCopyright?.cloneNode(true) as HTMLElement);
            copyright.classList.remove(HTMLCLASS.CONTAINER_IMPRESSUM_COPYRIGHT);
            copyright.classList.add(HTMLCLASS.SHOW_COPYRIGHT);
            FxxlPrint.frameContent.copyright = copyright;
        }
    }


    /**
     * Prepare print frame. Hide existing contents and add new contents for print
     *
     * @returns void
     */
    private static printPrepareFrame(): void {
        if (!FxxlPrint.printFrame || !FxxlPrint.frameContent || !FxxlPrint.bodyElements?.length) {
            FxxlPrint.readyForPrinting = false;
            return;
        }

        // HIDE: Body Elements ( CSS Print Mode )
        FxxlPrint.bodyElements.forEach((element: HTMLElement) => {
            element.classList.add(HTMLCLASS.HIDE);
        });

        // ADD: Print Content to Body

        // HEAD: Logo
        if (FxxlPrint.frameContent?.logo) {
            FxxlPrint.printFrame.appendChild(FxxlPrint.frameContent.logo);
        }

        // BODY: Print Main Content
        FxxlPrint.printFrame.appendChild(FxxlPrint.frameContent.mainContent);

        // FOOTER: Copyright
        if (FxxlPrint.frameContent?.copyright) {
            FxxlPrint.printFrame.appendChild(FxxlPrint.frameContent.copyright);
        }

        // FRAME: Add Body Print Class
        FxxlPrint.printFrame.classList.add(HTMLCLASS.SHOW_BODY);
    }


    /**
     * Preload images before printing: force load all images and causes a small delay
     *
     * @returns Promise<void>
     */
    private static async printPreloadImages(): Promise<void> {
        if (!FxxlPrint.printFrame || !FxxlPrint.frameContent) {
            FxxlPrint.readyForPrinting = false;
            return;
        }

        const images = FxxlPrint.frameContent.mainContent.querySelectorAll('img');

        // 1. Disable lazy loading
        images.forEach(img => {
            img.loading = 'eager';
        });

        // 2. Force load images
        await Promise.all(
            Array.from(images).map(img => {
                if (img.complete && img.naturalWidth !== 0) {
                    return Promise.resolve();
                }

                return new Promise<void>(resolve => {
                    const src = img.currentSrc || img.src;

                    // Force reload
                    img.src = '';
                    img.src = src;

                    img.onload = () => resolve();
                    img.onerror = () => resolve();
                });
            })
        );

        // 3. Small delay ( Safari layout )
        await new Promise(r => setTimeout(r, 50));
    }

    /**
     * Clean up the DOM after print operation by removing the added elements and showing the hidden ones
     *
     * @returns void
     */
    private static printCleanup(): void {
        // CLEANUP: Layout
        document.body.classList.remove(HTMLCLASS.SHOW_BODY);
        FxxlPrint?.frameContent?.logo?.remove();
        FxxlPrint?.frameContent?.mainContent?.remove();
        FxxlPrint?.frameContent?.copyright?.remove();

        const bodyElements: NodeListOf<HTMLElement> = document.querySelectorAll(SELECTOR.ALL_BODY_ELEMENTS);
        bodyElements.forEach((element: HTMLElement) => {
            element.classList.remove(HTMLCLASS.HIDE);
        });

        // RESET: Vars
        FxxlPrint.printResetVars();
    }

    /**
     * Fully Reset All Variables for new print session
     *
     * @returns void
     */
    private static printResetVars(): void {
        FxxlPrint.isPrinting   = false;
        FxxlPrint.bodyElements = null;
        FxxlPrint.printFrame   = null;
        FxxlPrint.frameContent = null;
    }

}

